import { useEffect, useState } from "react";
import { ShieldCheck, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ColorField = ({ label, value, onChange, id }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-9 w-12 rounded border border-gray-200 cursor-pointer" data-testid={`badge-${id}-picker`} />
      <Input value={value} maxLength={7} onChange={(e) => onChange(e.target.value)} className="font-mono w-28" data-testid={`badge-${id}-input`} />
    </div>
  </div>
);

const Preview = ({ b }) => (
  <div className={`relative h-44 rounded-md border border-gray-200 bg-[repeating-linear-gradient(45deg,#F9FAFB,#F9FAFB_12px,#F3F4F6_12px,#F3F4F6_24px)] overflow-hidden`} data-testid="badge-preview">
    <span className="absolute top-3 left-1/2 -translate-x-1/2 text-xs text-gray-400">Anteprima pagina del negozio</span>
    <div className="absolute flex items-center gap-3 rounded-[10px] border border-gray-200 px-3.5 py-2.5 shadow-[0_6px_24px_rgba(17,24,39,.12)]"
      style={{ background: b.bg, color: b.text, bottom: Math.min(b.offset, 120), [b.position === "right" ? "right" : "left"]: 18 }}>
      <span className="h-10 w-10 rounded-full flex items-center justify-center text-white" style={{ background: b.accent }}><ShieldCheck size={22} /></span>
      <span className="leading-tight">
        <span className="block font-extrabold text-sm">{b.title}</span>
        {b.show_score && <span className="flex items-center gap-1 font-extrabold">4,8 <span className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={12} className="fill-amber-500 text-amber-500" />)}</span></span>}
        <span className="block text-[11px] opacity-75">{b.show_score ? `124 recensioni${b.subtitle ? ` · ${b.subtitle}` : ""}` : b.subtitle}</span>
      </span>
    </div>
  </div>
);

export const BadgeSettings = () => {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setB(r.data.badge)); }, []);
  if (!b) return null;
  const set = (k) => (v) => setB({ ...b, [k]: v });
  const save = async () => {
    try { const { data } = await api.put("/admin/settings/badge", b); setB(data); toast.success("Badge aggiornato: le modifiche appaiono sul sito entro 5 minuti"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="badge-settings">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Titolo</Label><Input maxLength={40} value={b.title} onChange={(e) => set("title")(e.target.value)} data-testid="badge-title-input" /></div>
          <div className="space-y-1.5"><Label>Sottotitolo</Label><Input maxLength={60} value={b.subtitle} onChange={(e) => set("subtitle")(e.target.value)} data-testid="badge-subtitle-input" /></div>
        </div>
        <div className="flex flex-wrap gap-4">
          <ColorField id="bg" label="Sfondo" value={b.bg} onChange={set("bg")} />
          <ColorField id="text" label="Testo" value={b.text} onChange={set("text")} />
          <ColorField id="accent" label="Sigillo" value={b.accent} onChange={set("accent")} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Posizione</Label>
            <Select value={b.position} onValueChange={set("position")}>
              <SelectTrigger data-testid="badge-position-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">In basso a sinistra</SelectItem><SelectItem value="right">In basso a destra</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Distanza dal fondo (px)</Label><Input type="number" min="0" max="200" value={b.offset} onChange={(e) => set("offset")(Number(e.target.value))} data-testid="badge-offset-input" /></div>
        </div>
        <label className="flex items-center gap-3 text-sm"><Switch checked={b.show_score} onCheckedChange={set("show_score")} data-testid="badge-score-switch" /> Mostra voto e numero recensioni</label>
        <label className="flex items-center gap-3 text-sm"><Switch checked={b.show_mobile} onCheckedChange={set("show_mobile")} data-testid="badge-mobile-switch" /> Mostra anche su smartphone</label>
        <div className="flex gap-2">
          <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={save} data-testid="badge-save-button">Salva badge</Button>
          <Button variant="outline" onClick={() => setB({ title: "Garanzia Rocard", subtitle: "Acquisto protetto", bg: "#FFFFFF", text: "#242424", accent: "#0584D5", position: "left", offset: 18, show_score: true, show_mobile: true })} data-testid="badge-reset-button">Ripristina</Button>
        </div>
      </div>
      <Preview b={b} />
    </div>
  );
};
